/**
 * Singleton WebSocket client for the agent's `/ws` stream.
 *
 * Owns:
 *   - one underlying `WebSocket` connection per page
 *   - auto-reconnect with exponential backoff (1s → 30s cap)
 *   - subscribe/unsubscribe routing so multiple hooks can share a single
 *     `<deviceId>:<sensorId>` slot on the wire without re-subscribing
 *
 * Designed as a tiny event hub the React hooks layer wraps in
 * `useSyncExternalStore`-style subscribers. The class itself has no
 * React dependency, which keeps it testable from plain Vitest.
 *
 * The agent multiplexes `reading | outletState | signals:catalog` frames
 * on the same socket. Reading subscribers get only the messages they
 * asked for; broadcast frames (`outletState`) are forwarded to every
 * listener registered for that frame type.
 */
import type {
  WsClientMessage,
  WsOutletStateMessage,
  WsReadingMessage,
  WsServerMessage,
} from '@reefapp/shared';

export type WsConnectionStatus = 'connecting' | 'open' | 'closed' | 'error';

export interface WsClientOptions {
  url: string;
  /** Initial reconnect delay (ms). Default 1000. */
  initialBackoffMs?: number;
  /** Maximum reconnect delay (ms). Default 30_000. */
  maxBackoffMs?: number;
  /**
   * WebSocket constructor. Defaulted to `globalThis.WebSocket` but
   * injectable so unit tests can substitute a stub.
   */
  WebSocketCtor?: typeof WebSocket;
  /**
   * Schedule a one-shot reconnect attempt. Defaults to `setTimeout` but
   * injectable so tests can drive timing deterministically.
   */
  setTimer?: (cb: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (handle: ReturnType<typeof setTimeout>) => void;
}

type ReadingListener = (msg: WsReadingMessage) => void;
type OutletListener = (msg: WsOutletStateMessage) => void;
type StatusListener = (status: WsConnectionStatus) => void;

function subKey(deviceId: string, sensorId: string): string {
  return `${deviceId}:${sensorId}`;
}

const DEFAULT_INITIAL_BACKOFF_MS = 1_000;
const DEFAULT_MAX_BACKOFF_MS = 30_000;

export class WsClient {
  private ws: WebSocket | null = null;
  private status: WsConnectionStatus = 'closed';
  private backoffMs: number;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  private readonly readingListeners = new Map<string, Set<ReadingListener>>();
  private readonly outletListeners = new Set<OutletListener>();
  private readonly statusListeners = new Set<StatusListener>();

  private readonly url: string;
  private readonly initialBackoffMs: number;
  private readonly maxBackoffMs: number;
  private readonly WebSocketCtor: typeof WebSocket;
  private readonly setTimer: (
    cb: () => void,
    delayMs: number,
  ) => ReturnType<typeof setTimeout>;
  private readonly clearTimer: (
    handle: ReturnType<typeof setTimeout>,
  ) => void;

  constructor(opts: WsClientOptions) {
    this.url = opts.url;
    this.initialBackoffMs = opts.initialBackoffMs ?? DEFAULT_INITIAL_BACKOFF_MS;
    this.maxBackoffMs = opts.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS;
    this.backoffMs = this.initialBackoffMs;
    this.WebSocketCtor = opts.WebSocketCtor ?? (globalThis.WebSocket as typeof WebSocket);
    this.setTimer =
      opts.setTimer ??
      ((cb, delayMs) => setTimeout(cb, delayMs));
    this.clearTimer = opts.clearTimer ?? ((handle) => clearTimeout(handle));
  }

  /** Open the underlying WebSocket. Idempotent. */
  connect(): void {
    if (this.stopped) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.setStatus('connecting');
    let ws: WebSocket;
    try {
      ws = new this.WebSocketCtor(this.url);
    } catch (err) {
      this.setStatus('error');
      this.scheduleReconnect();
      // Swallow construction errors — reconnect will retry.
      void err;
      return;
    }
    this.ws = ws;
    ws.addEventListener('open', () => {
      if (this.ws !== ws) return;
      this.backoffMs = this.initialBackoffMs;
      this.setStatus('open');
      // Re-issue every active reading subscription on (re)connect.
      for (const key of this.readingListeners.keys()) {
        const [deviceId, sensorId] = key.split(':', 2) as [string, string];
        this.sendRaw({ type: 'subscribe', deviceId, sensorId });
      }
    });
    ws.addEventListener('message', (ev: MessageEvent) => {
      if (this.ws !== ws) return;
      this.handleMessage(ev.data);
    });
    ws.addEventListener('close', () => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.setStatus('closed');
      this.scheduleReconnect();
    });
    ws.addEventListener('error', () => {
      if (this.ws !== ws) return;
      this.setStatus('error');
      // The browser will fire `close` after `error`; reconnect is scheduled there.
    });
  }

  /** Tear down the client. Cancels reconnects + closes the socket. */
  close(): void {
    this.stopped = true;
    if (this.reconnectTimer !== null) {
      this.clearTimer(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // best-effort
      }
      this.ws = null;
    }
    this.readingListeners.clear();
    this.outletListeners.clear();
    this.statusListeners.clear();
    this.setStatus('closed');
  }

  /** Subscribe to a `(deviceId, sensorId)` time-series stream. */
  subscribeReading(
    deviceId: string,
    sensorId: string,
    listener: ReadingListener,
  ): () => void {
    const key = subKey(deviceId, sensorId);
    let bucket = this.readingListeners.get(key);
    const isFirst = !bucket;
    if (!bucket) {
      bucket = new Set();
      this.readingListeners.set(key, bucket);
    }
    bucket.add(listener);
    if (isFirst && this.status === 'open') {
      this.sendRaw({ type: 'subscribe', deviceId, sensorId });
    }
    return (): void => {
      const cur = this.readingListeners.get(key);
      if (!cur) return;
      cur.delete(listener);
      if (cur.size === 0) {
        this.readingListeners.delete(key);
        if (this.status === 'open') {
          this.sendRaw({ type: 'unsubscribe', deviceId, sensorId });
        }
      }
    };
  }

  /** Subscribe to every `outletState` broadcast frame. */
  subscribeOutletState(listener: OutletListener): () => void {
    this.outletListeners.add(listener);
    return (): void => {
      this.outletListeners.delete(listener);
    };
  }

  /** Subscribe to connection-status transitions. */
  subscribeStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    // Fire once with the current status so consumers don't have to
    // separately read `getStatus()` after mounting.
    listener(this.status);
    return (): void => {
      this.statusListeners.delete(listener);
    };
  }

  /** Read the current connection status (synchronous). */
  getStatus(): WsConnectionStatus {
    return this.status;
  }

  /** Test/debug helper — active reading-subscription count. */
  get activeSubscriptionCount(): number {
    return this.readingListeners.size;
  }

  private handleMessage(data: unknown): void {
    let parsed: unknown;
    try {
      const raw = typeof data === 'string' ? data : String(data);
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object') return;
    const msg = parsed as WsServerMessage;
    if (msg.type === 'reading') {
      const bucket = this.readingListeners.get(subKey(msg.deviceId, msg.sensorId));
      if (bucket) {
        for (const fn of bucket) fn(msg);
      }
      return;
    }
    if (msg.type === 'outletState') {
      for (const fn of this.outletListeners) fn(msg);
    }
  }

  private sendRaw(msg: WsClientMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(msg));
    } catch {
      // Browser will surface this via the error/close events.
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    if (this.reconnectTimer !== null) return;
    const delay = this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
    this.reconnectTimer = this.setTimer(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private setStatus(next: WsConnectionStatus): void {
    if (this.status === next) return;
    this.status = next;
    for (const fn of this.statusListeners) fn(next);
  }
}

// ─────────────────────────────────────────────────────────────────────
// Module-level singleton (one connection per page).
//
// Lazy: the first hook to mount creates the client. `__resetWsClientForTests`
// is exported so tests can isolate their assertions from cross-file state.
// ─────────────────────────────────────────────────────────────────────

let singleton: WsClient | null = null;

export function getWsClient(url?: string): WsClient {
  if (singleton) return singleton;
  if (!url) throw new Error('getWsClient: url required for first call');
  singleton = new WsClient({ url });
  singleton.connect();
  return singleton;
}

export function setWsClientForTests(client: WsClient | null): void {
  if (singleton && singleton !== client) {
    try {
      singleton.close();
    } catch {
      // ignore
    }
  }
  singleton = client;
}